import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../../contexts/PopupContext';
import { fetchUserActivityStats } from '../../../utils/profileStatsService';
import NotificationMessage from '../NotificationMessage/NotificationMessage';
import { formatNotificationTime } from '../utils/formatNotificationTime';
import { getNotificationIcon } from '../utils/getNotificationIcon';
import { NotificationIconGlyph } from '../NotificationIcons';
import './NotificationItem.css';

function NotificationItem({ notification, onMarkAsRead, onDelete }) {
  const { t } = useTranslation('notifications');
  const { openPopup } = usePopup();
  const icon = getNotificationIcon(notification.type);

  const handleClick = () => {
    // Mark as read if not already read
    if (!notification.read) {
      onMarkAsRead(notification.id, { stopPropagation: () => {} });
    }

    // Navigate to related resource based on notification type
    const { type, event_id, data, event } = notification;

    switch (type) {
      case 'application_received':
      case 'application_accepted':
      case 'application_rejected':
      case 'event_created':
      case 'event_starts_soon':
      case 'event_started':
      case 'event_cancelled':
      case 'event_invitation':
      case 'participant_kicked':
      case 'friend_joined_event':
        // Powiadomienia związane z eventem - otwórz popup eventu (bez zmiany zakładki)
        if (event_id || event?.id) {
          const eventId = event_id || event?.id;
          openPopup('event-details', { eventId }, 0);
        }
        break;

      case 'friend_invitation':
        // Powiadomienie o zaproszeniu do znajomych - otwórz profil sendera
        if (data?.sender_id) {
          openPopup('profile', { userId: data.sender_id }, 0);
        }
        break;

      case 'friendship_accepted':
        // Powiadomienie o akceptacji zaproszenia - otwórz profil znajomego
        if (data?.friend_id) {
          openPopup('profile', { userId: data.friend_id }, 0);
        }
        break;

      case 'badge_unlocked': {
        // Pusty stats powodował, że w popupie wszystkie odznaki aktywności były „niezdobyte” — został tylko wiersz ról (np. admin).
        const recipientId = notification.user_id;
        void (async () => {
          const stats = recipientId ? await fetchUserActivityStats(recipientId) : {};
          openPopup(
            'achievements',
            {
              userRole: data?.account_user_role || 'user',
              stats,
            },
            0
          );
        })();
        break;
      }

      default:
        // Dla innych typów - spróbuj otworzyć event jeśli jest dostępny (bez zmiany zakładki)
        if (event_id || event?.id) {
          const eventId = event_id || event?.id;
          openPopup('event-details', { eventId }, 0);
        }
        break;
    }
  };

  const handleMarkAsReadClick = (e) => {
    e.stopPropagation();
    onMarkAsRead(notification.id, e);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(notification.id, e);
  };

  const isRemoving = Boolean(notification._removing);

  return (
    <div
      className={`notification-item ${notification.read ? 'read' : 'unread'}${
        isRemoving ? ' notification-item--removing' : ''
      }`}
      data-notification-type={notification.type}
      data-notification-group={icon.group}
      onClick={handleClick}
    >
      <div className="notification-content">
        <div className="notification-icon">
          <span className={`notification-icon-badge notification-icon-badge--${icon.group}`}>
            <NotificationIconGlyph icon={icon.icon} />
          </span>
        </div>
        <div className="notification-text">
          <p className="notification-message">
            <NotificationMessage 
              notification={notification} 
              t={t}
              onOpenProfile={(userId) => {
                if (userId) {
                  openPopup('profile', { userId }, 0);
                }
              }}
              onOpenEvent={(eventId) => {
                if (eventId) {
                  openPopup('event-details', { eventId }, 0);
                }
              }}
            />
          </p>
          <span className="notification-time">
            {formatNotificationTime(notification.created_at)}
          </span>
        </div>
      </div>
      <div className="notification-actions">
        {!notification.read && (
          <button
            className="notification-action-btn"
            onClick={handleMarkAsReadClick}
            title={t('notifications.mark_as_read') || 'Oznacz jako przeczytane'}
          >
            ✓
          </button>
        )}
        <button
          className="notification-action-btn delete-btn"
          onClick={handleDeleteClick}
          title={t('notifications.delete') || 'Usuń'}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default NotificationItem;
